import { create } from 'zustand';
import { env } from '@/config/env';

/**
 * Multi-tenant resolution.
 *
 * Modo `fixed` (por defecto): el tenant es `VITE_DEFAULT_TENANT`. Es lo correcto
 * cuando el host es la URL del ENTORNO y no identifica a un tenant (p. ej.
 * `ambulancias-desa.domainlogic.io` — "ambulancias-desa" NO es un tenant).
 *
 * Modo `subdomain` (`VITE_TENANT_MODE=subdomain`): el primer label del host es
 * el tenant (`pozuelo.ambulancias.app` -> `pozuelo`), con fallback al default
 * para IPs/localhost. Activar solo en instalaciones con subdominio por tenant.
 *
 * El api client envía el tenant activo como cabecera `X-TenantID` en TODAS las
 * llamadas (también el login): un tenant inexistente provoca 400 en el backend.
 */
function resolveTenantFromHost(): string {
  if (env.tenantMode !== 'subdomain') return env.defaultTenant;
  if (typeof window === 'undefined') return env.defaultTenant;
  const host = window.location.hostname;
  // ignore IPs and localhost -> use env default
  const isIp = /^\d{1,3}(\.\d{1,3}){3}$/.test(host);
  if (isIp || host === 'localhost' || host.endsWith('.local')) {
    return env.defaultTenant;
  }
  const parts = host.split('.');
  if (parts.length > 2) return parts[0];
  return env.defaultTenant;
}

interface TenantState {
  tenant: string;
  setTenant: (tenant: string) => void;
}

export const useTenantStore = create<TenantState>((set) => ({
  tenant: resolveTenantFromHost(),
  setTenant: (tenant) => set({ tenant }),
}));

export const getTenant = () => useTenantStore.getState().tenant;
